
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ExpertApplication {
  id: string;
  user_id: string | null;
  full_name: string;
  email: string;
  phone: string | null;
  professional_title: string;
  years_experience: number;
  specializations: string[];
  bio: string;
  hourly_rate: number;
  linkedin_url: string | null;
  website_url: string | null;
  motivation_letter: string | null;
  status: 'pending' | 'under_review' | 'approved' | 'rejected';
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useExpertApplicationEdit(application: ExpertApplication) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedApplication, setEditedApplication] = useState(application);
  const [loading, setLoading] = useState(false);

  const handleSaveEdit = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from("expert_applications")
        .update({
          full_name: editedApplication.full_name,
          professional_title: editedApplication.professional_title,
          bio: editedApplication.bio,
          hourly_rate: editedApplication.hourly_rate,
          linkedin_url: editedApplication.linkedin_url,
          website_url: editedApplication.website_url,
          specializations: editedApplication.specializations,
        })
        .eq("id", application.id);

      if (error) throw error;

      // If there's an active expert profile, update it too
      if (application.status === 'approved') {
        const { error: expertError } = await supabase
          .from("consultation_experts")
          .update({
            full_name: editedApplication.full_name,
            professional_title: editedApplication.professional_title,
            bio: editedApplication.bio,
            hourly_rate: editedApplication.hourly_rate,
            linkedin_url: editedApplication.linkedin_url,
            website_url: editedApplication.website_url,
            specializations: editedApplication.specializations,
          })
          .eq("application_id", application.id);

        if (expertError && expertError.code !== 'PGRST116') {
          throw expertError;
        }
      }

      toast.success("Application updated successfully");
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating application:", error);
      toast.error("Failed to update application");
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditedApplication(application);
  };

  return {
    isEditing,
    setIsEditing,
    editedApplication,
    setEditedApplication,
    loading,
    handleSaveEdit,
    cancelEdit
  };
}
